/**
 * Wire hub profile + Profile screen to Firebase Auth + Razorpay shop.
 */
import {
  getAccountSnapshot,
  initAuthAccount,
  isAuthHydrated,
  markCurrentUserAdminLocally,
  saveAccountDisplayName,
  saveAccountPhoto,
  signInAsGuest,
  signInWithGoogle,
  signOutAccount,
  subscribeAccount,
  type AccountSnapshot,
} from "./firebase/authAccount";
import { isFirebaseConfigured } from "./firebase/config";
import { isAllowlistedAdminUid } from "./firebase/adminAllowlist";
import { httpsCallable } from "firebase/functions";
import { getFunctionsAsia } from "./firebase/functions";
import { SHOP_PRODUCTS, CHECKOUT_SITE, type ShopProductId } from "./shop/catalog";
import { maybeResumeWebsiteCheckout, startPurchase } from "./shop/checkout";
import { formatBuyLabel } from "./shop/regionalPricing";
import { Capacitor } from "@capacitor/core";

export type LocalProfile = { name: string; photo: string | null };

type BindOpts = {
  profileName: HTMLInputElement;
  profileAvatar: HTMLImageElement;
  getLocal: () => LocalProfile;
  setLocal: (p: LocalProfile) => void;
  defaultAvatar: (name: string) => string;
  onToast?: (msg: string) => void;
  onPremiumUnlock?: () => void;
  onAccountChange?: (snap: AccountSnapshot) => void;
};

function setText(id: string, text: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}

function paintShop(snap: AccountSnapshot): void {
  const armoryBadge = document.getElementById("armory-badge");
  if (armoryBadge) {
    armoryBadge.textContent = snap.plan === "full_course" ? "Premium" : "Shop live";
  }
  const armoryBadgeProfile = document.getElementById("armory-badge-profile");
  if (armoryBadgeProfile) {
    armoryBadgeProfile.textContent = snap.plan === "full_course" ? "Premium" : "Open";
  }

  document.querySelectorAll<HTMLButtonElement>("[data-buy]").forEach((btn) => {
    const id = btn.dataset.buy as ShopProductId;
    const card = btn.closest(".armory-card");
    if (id === "full_course" && !snap.isAnonymous && snap.plan === "full_course") {
      btn.textContent = "Owned";
      btn.disabled = true;
      card?.classList.remove("is-locked");
      card?.classList.add("is-owned");
    } else if (id === "hd_sample_pack" && snap.hdSamplePack) {
      btn.textContent = "Owned";
      btn.disabled = true;
      card?.classList.remove("is-locked");
      card?.classList.add("is-owned");
    } else if (SHOP_PRODUCTS[id] && !SHOP_PRODUCTS[id].available) {
      btn.textContent = "Coming Soon";
      btn.disabled = true;
      card?.classList.add("is-locked");
      card?.classList.remove("is-owned");
    } else if (SHOP_PRODUCTS[id]) {
      const canBuy = snap.configured && Boolean(snap.user) && !snap.isAnonymous;
      btn.textContent = canBuy
        ? formatBuyLabel(id)
        : "Sign in with Google to Buy";
      btn.disabled = !canBuy;
      card?.classList.add("is-locked");
      card?.classList.remove("is-owned");
    }
  });
}

function applyAvatar(opts: BindOpts, name: string, photo: string | null): void {
  const src = photo || opts.defaultAvatar(name);
  const apply = (img: HTMLImageElement | null) => {
    if (!img) return;
    img.referrerPolicy = "no-referrer";
    img.decoding = "async";
    img.onerror = () => {
      img.onerror = null;
      img.src = opts.defaultAvatar(name);
    };
    img.src = src;
  };
  apply(opts.profileAvatar);
  apply(document.querySelector<HTMLImageElement>("#profile-edit-avatar"));
}

function setAuthButtons(kind: "google" | "guest" | "out", show: boolean): void {
  document.querySelectorAll<HTMLElement>(`[data-auth="${kind}"]`).forEach((el) => {
    el.hidden = !show;
  });
}

function paintAdminChrome(snap: AccountSnapshot): void {
  const show = Boolean(snap.isAdmin);
  document.querySelectorAll<HTMLElement>("[data-admin-only]").forEach((el) => {
    if (show) el.removeAttribute("hidden");
    else el.setAttribute("hidden", "");
  });
}

/** Sync server entitlements for allowlisted admins only (never for normal users). */
let adminRoleSyncTried = false;
async function syncAdminRoleFromServer(toast: (m: string) => void): Promise<void> {
  if (adminRoleSyncTried) return;
  adminRoleSyncTried = true;
  try {
    const fn = httpsCallable(getFunctionsAsia(), "adminBootstrap");
    await fn({});
    markCurrentUserAdminLocally();
    toast("Admin access synced.");
  } catch {
    /* server deny — client allowlist may still show badge */
  }
}

export async function openAdminPortal(): Promise<void> {
  const url = `${CHECKOUT_SITE}/admin.html?v=2`;
  try {
    if (Capacitor.isNativePlatform()) {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url });
      return;
    }
  } catch {
    /* fall through */
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

function paint(snap: AccountSnapshot, opts: BindOpts): void {
  try {
    opts.onAccountChange?.(snap);
  } catch (e) {
    console.warn("[accountUi] onAccountChange", e);
  }
  const status = document.getElementById("auth-status");
  const statusProfile = document.getElementById("auth-status-profile");
  const emailEl = document.getElementById("auth-email");
  const planEl = document.getElementById("auth-plan");
  const cfgHint = document.getElementById("auth-config-hint");
  const profileEditName = document.querySelector<HTMLInputElement>("#profile-edit-name");
  const saveHint = document.getElementById("profile-save-hint");

  // Only show the “Firebase not configured” line when env vars are actually missing.
  if (cfgHint) {
    cfgHint.hidden = snap.configured;
    cfgHint.style.display = snap.configured ? "none" : "";
  }

  // Wait for Firebase Auth to restore the persisted session before saying Signed out.
  if (snap.configured && !isAuthHydrated()) {
    if (status) status.textContent = "Restoring session…";
    if (statusProfile) statusProfile.textContent = "Restoring session…";
    setAuthButtons("google", false);
    setAuthButtons("guest", false);
    setAuthButtons("out", false);
    paintAdminChrome(snap);
    return;
  }

  if (!snap.user) {
    const statusText = snap.configured ? "Signed out" : "Offline profile";
    if (status) status.textContent = statusText;
    if (statusProfile) statusProfile.textContent = statusText;
    if (emailEl) emailEl.textContent = "—";
    if (planEl) planEl.textContent = "FREE · 5 lessons";
    setText("auth-plan-detail", "FREE · 5 lessons");
    const local = opts.getLocal();
    setText("profile-account-name", local.name || "—");
    if (profileEditName) profileEditName.value = local.name || "";
    opts.profileName.value = local.name || "";
    applyAvatar(opts, local.name || "PLAYER", local.photo);
    setAuthButtons("google", snap.configured);
    setAuthButtons("guest", snap.configured);
    setAuthButtons("out", false);
    if (saveHint) saveHint.textContent = "Sign in to sync name & photo across devices.";
    paintShop(snap);
    paintAdminChrome(snap);
    return;
  }

  const local = opts.getLocal();
  // Signed-in: account (Google + Firestore) always wins over stale local cache.
  // Local data:-URL only kept if account has no photo yet.
  const name =
    snap.displayName ||
    local.name ||
    (snap.isAnonymous ? "Guest Player" : "PLAYER");
  const photo = snap.photoURL || local.photo;

  const statusText = snap.isAnonymous ? "Playing as Guest" : "Signed in";
  if (status) status.textContent = statusText;
  if (statusProfile) statusProfile.textContent = statusText;
  if (emailEl) emailEl.textContent = snap.email || (snap.isAnonymous ? "Guest session" : "—");
  const planLabel =
    !snap.isAnonymous && snap.plan === "full_course"
      ? "FULL COURSE · Unlocked"
      : snap.isAnonymous
        ? "GUEST · Free access"
        : "FREE · 5 lessons";
  if (planEl) planEl.textContent = planLabel;
  setText("auth-plan-detail", planLabel);
  // Guest can upgrade to Google; Google account shows Sign out only.
  setAuthButtons("google", snap.isAnonymous);
  setAuthButtons("guest", false);
  setAuthButtons("out", true);

  // Always autofill hub + Profile tab editors after sign-in.
  opts.profileName.value = name;
  if (profileEditName) profileEditName.value = name;
  applyAvatar(opts, name, photo);
  opts.setLocal({ name, photo: photo ?? null });
  setText("profile-account-name", name);

  if (saveHint) {
    saveHint.textContent = snap.isAnonymous
      ? "Guest: name/photo stay on this device. Sign in with Google to sync."
      : snap.photoURL || snap.displayName
        ? "Synced from your Google account — edit anytime, then Save."
        : "Edit name or photo below, then Save — syncs to your account.";
  }
  paintShop(snap);
  paintAdminChrome(snap);
  // Only allowlisted admins hit adminBootstrap (entitlements) — never spam it for every user.
  if (snap.user && !snap.isAnonymous && isAllowlistedAdminUid(snap.user.uid)) {
    void syncAdminRoleFromServer(opts.onToast ?? (() => undefined));
  }

  if (!snap.isAnonymous && snap.plan === "full_course") {
    opts.onPremiumUnlock?.();
  }
}

/** Resize + JPEG-compress a picked image so Firestore stays cheap. */
export async function compressAvatarFile(file: File, maxPx = 192): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.82);
}

export async function bindAccountUi(opts: BindOpts): Promise<void> {
  const toast = opts.onToast ?? ((m: string) => console.info(m));

  // Show Sign in immediately when Firebase env is present (don't wait on Auth).
  if (isFirebaseConfigured) {
    setAuthButtons("google", true);
    setAuthButtons("guest", true);
    setAuthButtons("out", false);
  }

  const onGoogle = () => {
    void (async () => {
      try {
        const { promptReferralBeforeGoogleSignIn, applyReferralAfterSignIn } = await import(
          "./referralSignIn"
        );
        await promptReferralBeforeGoogleSignIn();
        const { isCapacitorAndroid } = await import("./firebase/androidAuthBridge");
        if (isCapacitorAndroid()) {
          toast("Opening Google Sign-In… complete it in the browser, then return here.");
        }
        await signInWithGoogle();
        const refMsg = await applyReferralAfterSignIn();
        toast(refMsg || "Signed in — profile filled from Google.");
      } catch (e) {
        toast(e instanceof Error ? e.message : "Sign-in failed");
      }
    })();
  };
  const onGuest = () => {
    void (async () => {
      try {
        await signInAsGuest();
        toast("Guest session ready. Sign in with Google when you want to purchase.");
      } catch (e) {
        toast(e instanceof Error ? e.message : "Guest sign-in failed");
      }
    })();
  };
  const onOut = () => {
    void (async () => {
      await signOutAccount();
      const local = opts.getLocal();
      applyAvatar(opts, local.name, local.photo);
      toast("Signed out.");
    })();
  };

  document.querySelectorAll("[data-auth='google']").forEach((el) => {
    el.addEventListener("click", onGoogle);
  });
  document.querySelectorAll("[data-auth='guest']").forEach((el) => {
    el.addEventListener("click", onGuest);
  });
  document.querySelectorAll("[data-auth='out']").forEach((el) => {
    el.addEventListener("click", onOut);
  });
  document.querySelectorAll("[data-admin-portal]").forEach((el) => {
    el.addEventListener("click", () => {
      void openAdminPortal().catch((e) => {
        toast(e instanceof Error ? e.message : "Could not open admin portal");
      });
    });
  });

  const syncNameLocal = (raw: string) => {
    const name = raw.trim().slice(0, 24) || "PLAYER ONE";
    opts.profileName.value = name;
    const profileEditName = document.querySelector<HTMLInputElement>("#profile-edit-name");
    if (profileEditName) profileEditName.value = name;
    const prev = opts.getLocal();
    opts.setLocal({ ...prev, name });
    if (!prev.photo) applyAvatar(opts, name, null);
    setText("profile-account-name", name);
  };

  opts.profileName.addEventListener("change", () => {
    syncNameLocal(opts.profileName.value);
    const snap = getAccountSnapshot();
    if (snap.user && !snap.isAnonymous) {
      void saveAccountDisplayName(opts.profileName.value).catch((e) =>
        toast(e instanceof Error ? e.message : "Could not save name"),
      );
    }
  });

  document.querySelector("#profile-edit-name")?.addEventListener("change", (e) => {
    const input = e.target as HTMLInputElement;
    syncNameLocal(input.value);
  });

  document.querySelector("#btn-profile-save")?.addEventListener("click", () => {
    void (async () => {
      const snap = getAccountSnapshot();
      const name =
        document.querySelector<HTMLInputElement>("#profile-edit-name")?.value ??
        opts.profileName.value;
      syncNameLocal(name);
      const local = opts.getLocal();
      try {
        if (snap.user && !snap.isAnonymous) {
          await saveAccountDisplayName(local.name);
          if (local.photo?.startsWith("data:")) {
            await saveAccountPhoto(local.photo);
          }
          toast("Profile saved.");
        } else {
          toast("Saved on this device.");
        }
      } catch (e) {
        toast(e instanceof Error ? e.message : "Save failed");
      }
    })();
  });

  document.querySelector("#btn-profile-avatar")?.addEventListener("click", () => {
    document.querySelector<HTMLInputElement>("#avatar-file")?.click();
  });

  document.querySelector("#btn-profile-use-google-photo")?.addEventListener("click", () => {
    void (async () => {
      const snap = getAccountSnapshot();
      const googlePhoto = snap.user?.photoURL || null;
      if (!googlePhoto) {
        toast("No Google photo on this account.");
        return;
      }
      opts.setLocal({ ...opts.getLocal(), photo: googlePhoto });
      applyAvatar(opts, opts.getLocal().name, googlePhoto);
      try {
        if (snap.user && !snap.isAnonymous) {
          await saveAccountPhoto(googlePhoto);
        }
        toast("Using Google photo.");
      } catch (e) {
        toast(e instanceof Error ? e.message : "Could not update photo");
      }
    })();
  });

  document.querySelectorAll<HTMLButtonElement>("[data-buy]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.buy as ShopProductId;
      if (!id || !SHOP_PRODUCTS[id] || !SHOP_PRODUCTS[id].available) return;
      btn.disabled = true;
      void (async () => {
        try {
          const result = await startPurchase(id, toast);
          toast(result.message);
        } catch (e) {
          toast(e instanceof Error ? e.message : "Checkout failed");
        } finally {
          paintShop(getAccountSnapshot());
        }
      })();
    });
  });

  document.querySelectorAll("[data-coming-soon]").forEach((btn) => {
    btn.addEventListener("click", () => {
      toast("Coming soon.");
    });
  });

  const ready = document.getElementById("firebase-ready-pill");
  if (ready) {
    ready.textContent = isFirebaseConfigured ? "Online" : "Offline";
    ready.classList.toggle("is-live", isFirebaseConfigured);
  }

  subscribeAccount((snap) => paint(snap, opts));
  await initAuthAccount();
  void maybeResumeWebsiteCheckout(toast);
}
